'use client';
import { IConversationSet, IFanConversationSet } from '@ui/components';
import { useMemo } from 'react';

interface UseCommunityTabsProps {
  creatorDisplayName?: string;
  conversationType?: string;
  hasCreatorEnabledTopics?: boolean;
  hasCreatorEnabledFansCanCreatePosts?: boolean;
  isPostACrewPost?: boolean;
  isTopicSelected?: boolean;
  onTabChange?: (tab: string) => void;
  initialSelectedTab?: number;
  customSecondTabLabel?: string;
  defaultCommunitySet?: IConversationSet | null;
}

interface UseCommunityTabsReturn {
  tabs: string[];
  selectedTab: number;
  resourceTabs: Array<{
    label: string;
    labelTextAlias: string;
    title: string;
    content: React.ReactNode;
  }>;
  onTabChanged: (tab: string) => void;
}

export const useCommunityTabs = ({
  creatorDisplayName,
  conversationType,
  hasCreatorEnabledTopics,
  hasCreatorEnabledFansCanCreatePosts,
  isPostACrewPost,
  isTopicSelected,
  onTabChange,
  initialSelectedTab,
  customSecondTabLabel,
  defaultCommunitySet,
}: UseCommunityTabsProps): UseCommunityTabsReturn => {
  const tabs = useMemo(() => {
    if (!creatorDisplayName) return [];

    // If defaultCommunitySet is null/undefined, return empty to prevent stale data
    if (!defaultCommunitySet) return [];

    // Use custom labels from community_miscellaneous if available, otherwise fallback to defaults
    const post_creator_segment_label =
      defaultCommunitySet?.conversation?.miscellaneous?.post_creator_segment_label ||
      creatorDisplayName;
    const post_community_segment =
      defaultCommunitySet?.conversation?.miscellaneous?.post_community_segment || 'crew';
    const topic_community_segment =
      defaultCommunitySet?.conversation?.miscellaneous?.topics_tab_label || 'Topic';

    const tabList = [post_creator_segment_label];

    if (hasCreatorEnabledTopics) {
      tabList.push(topic_community_segment);
    } else if (hasCreatorEnabledFansCanCreatePosts) {
      tabList.push(post_community_segment);
    } else if (customSecondTabLabel) {
      tabList.push(customSecondTabLabel);
    } else {
      tabList;
    }
    return tabList;
  }, [
    creatorDisplayName,
    hasCreatorEnabledTopics,
    customSecondTabLabel,
    defaultCommunitySet,
    hasCreatorEnabledFansCanCreatePosts,
  ]);

  const selectedTab = useMemo(() => {
    if (tabs.length === 0) {
      return 0;
    }

    if (initialSelectedTab !== undefined) {
      // Ensure the initialSelectedTab is within bounds of the tabs array
      return Math.min(Math.max(initialSelectedTab, 0), tabs.length - 1);
    }

    if (
      isTopicSelected ||
      conversationType === 'topic' ||
      (isPostACrewPost && !hasCreatorEnabledTopics)
    ) {
      return 1;
    }
    return 0;
  }, [
    tabs.length,
    conversationType,
    initialSelectedTab,
    isPostACrewPost,
    hasCreatorEnabledTopics,
    isTopicSelected,
  ]);

  const onTabChanged = (tab: string) => {
    const tabIndex = tabs.indexOf(tab);
    if (tabIndex === -1) return;

    onTabChange?.(tab);
  };

  const resourceTabs = useMemo(() => {
    if (!creatorDisplayName || tabs.length === 0) {
      return [];
    }

    return tabs.map((tab, index) => ({
      label: tab,
      labelTextAlias: tab,
      title:
        index === 0 ? 'Posts' : hasCreatorEnabledTopics && !isTopicSelected ? 'Topics' : 'Posts',
      content: null,
    }));
  }, [creatorDisplayName, tabs, hasCreatorEnabledTopics, isTopicSelected]);

  return {
    tabs,
    selectedTab,
    resourceTabs,
    onTabChanged,
  };
};
